import { useCallback, useEffect, useRef, useState } from 'react';
import { describeError, pluginSchemas, problemType } from '../../lib/payPolicyTemplates/api';
import { requiredDependencies, SchemaMap } from './templateDraft';
import { PluginSchema, TemplatePlugin } from './types';

/** Why Global Calculations cannot describe a plugin, from its answer to a schema request; null when the request just failed. */
const unreadableReason = (caught: { status?: number }): string | null => {
  if (caught?.status === 422 && problemType(caught)?.endsWith(':plugin_not_found')) return 'Global Calculations no longer has it';
  // 422 for a range no version satisfies, 400 for a range it cannot read: its own words say which.
  if (caught?.status === 422 || caught?.status === 400) return describeError(caught);
  return null;
};

/**
 * The schemas of the template's plugins and of everything they load: each schema that arrives names
 * its dependencies, which are fetched in turn. One plugin Global Calculations cannot describe fails a
 * whole batch, so a failed batch is retried a plugin at a time to name those and say why. A request
 * that fails for another reason (a 5xx, the network) is kept in `failed`, so the editor can retry it.
 */
export const usePluginSchemas = (plugins: TemplatePlugin[]) => {
  const [schemas, setSchemas] = useState<SchemaMap>({});
  const [unreadable, setUnreadable] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<Record<string, TemplatePlugin>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requested = useRef(new Set<string>());
  const inFlight = useRef(0);

  const fetchSchemas = useCallback((wanted: TemplatePlugin[]) => {
    const pending = wanted.filter((plugin, index) => !requested.current.has(plugin.pluginId) && wanted.findIndex((other) => other.pluginId === plugin.pluginId) === index);
    if (pending.length === 0) return;
    pending.forEach((plugin) => requested.current.add(plugin.pluginId));
    inFlight.current += 1;
    setLoading(true);

    const store = (found: PluginSchema[]) => {
      setSchemas((previous) => {
        const next = { ...previous };
        found.forEach((schema) => {
          next[schema.id] = schema;
        });
        return next;
      });
      fetchSchemas(found.flatMap((schema) => requiredDependencies(schema).map((dependency) => ({ pluginId: dependency.plugin, version: dependency.version }))));
    };

    pluginSchemas(pending)
      .then(store)
      .catch(async () => {
        const reasons: Record<string, string> = {};
        const retryable: Record<string, TemplatePlugin> = {};
        await Promise.all(
          pending.map((plugin) =>
            pluginSchemas([plugin])
              .then(store)
              .catch((caught: { status?: number }) => {
                const reason = unreadableReason(caught);
                if (reason !== null) reasons[plugin.pluginId] = reason;
                else {
                  // Forgotten, so a retry asks for it again.
                  requested.current.delete(plugin.pluginId);
                  retryable[plugin.pluginId] = plugin;
                  setError(describeError(caught));
                }
              })
          )
        );
        if (Object.keys(reasons).length > 0) setUnreadable((previous) => ({ ...previous, ...reasons }));
        if (Object.keys(retryable).length > 0) setFailed((previous) => ({ ...previous, ...retryable }));
      })
      .finally(() => {
        inFlight.current -= 1;
        if (inFlight.current === 0) setLoading(false);
      });
  }, []);

  const retry = useCallback(() => {
    const again = Object.values(failed);
    setFailed({});
    setError(null);
    fetchSchemas(again);
  }, [failed, fetchSchemas]);

  const key = plugins.map((plugin) => plugin.pluginId).join(',');
  useEffect(() => {
    fetchSchemas(plugins);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the plugin ids
  }, [key, fetchSchemas]);

  return { schemas, unreadable, failed, loading, error, retry };
};
